<template>
  <div class="duplicates">
    <v-container fluid class="px-6 py-4">
      <!-- Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="header-text">
            <h1 class="page-title">
              <v-icon large class="title-icon mr-3">mdi-account-multiple-minus</v-icon>
              Duplicate Detection
            </h1>
            <p class="page-subtitle">
              Find and manage duplicate records in your CRM data with AI-powered similarity analysis
            </p>
          </div>
          <v-btn
            large
            color="primary"
            class="scan-btn"
            @click="scanForDuplicates"
            :loading="isScanning"
            elevation="2"
          >
            <v-icon left>mdi-magnify-scan</v-icon>
            Scan for Duplicates
          </v-btn>
        </div>
      </div>

      <!-- Quick Stats -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper orange--bg">
                <v-icon size="24" color="white">mdi-account-multiple</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ stats.total_detections || 0 }}</div>
                <div class="stats-label">Duplicates Found</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper red--bg">
                <v-icon size="24" color="white">mdi-alert</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ stats.high_confidence_matches || 0 }}</div>
                <div class="stats-label">High Confidence</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper green--bg">
                <v-icon size="24" color="white">mdi-merge</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ stats.merged || 0 }}</div>
                <div class="stats-label">Records Merged</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper purple--bg">
                <v-icon size="24" color="white">mdi-clock</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ Math.round(stats.estimated_time_saved_hours || 0) }}h</div>
                <div class="stats-label">Time Saved</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filters -->
      <v-card class="filters-card mb-6" elevation="2">
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.status"
                :items="statusOptions"
                label="Status"
                clearable
                dense
                outlined
                @change="fetchDetections"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.record_type"
                :items="recordTypeOptions"
                label="Record Type"
                clearable
                dense
                outlined
                @change="fetchDetections"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.similarity_threshold"
                :items="similarityOptions"
                label="Min Similarity"
                clearable
                dense
                outlined
                @change="fetchDetections"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-checkbox
                v-model="filters.high_confidence_only"
                label="High Confidence Only"
                @change="fetchDetections"
              ></v-checkbox>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Duplicates Table -->
      <v-card elevation="4">
        <v-card-title class="pa-4">
          <h3>Duplicate Detections</h3>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            dense
            outlined
          ></v-text-field>
        </v-card-title>
        
        <v-data-table
          :headers="headers"
          :items="detections"
          :loading="isLoading"
          :search="search"
          class="elevation-0"
        >
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              dark
              small
              label
            >
              {{ formatStatus(item.status) }}
            </v-chip>
          </template>

          <template v-slot:[`item.record_type`]="{ item }">
            <v-chip
              color="blue"
              text-color="white"
              small
              label
            >
              {{ formatRecordType(item.record_type) }}
            </v-chip>
          </template>

          <template v-slot:[`item.similarity_score`]="{ item }">
            <div class="similarity-score">
              <v-progress-linear
                :value="item.similarity_score || 0"
                :color="getSimilarityColor(item.similarity_score)"
                height="6"
                rounded
                class="mb-1"
              ></v-progress-linear>
              <span class="score-text">{{ Math.round(item.similarity_score || 0) }}%</span>
            </div>
          </template>

          <template v-slot:[`item.is_high_confidence`]="{ item }">
            <v-icon :color="item.is_high_confidence ? 'green' : 'grey'">
              {{ item.is_high_confidence ? 'mdi-check-circle' : 'mdi-minus-circle' }}
            </v-icon>
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon
              small
              color="primary"
              @click="viewDetection(item)"
            >
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            <v-btn
              v-if="item.status === 'pending_review'"
              icon
              small
              color="success"
              @click="mergeRecords(item)"
            >
              <v-icon>mdi-merge</v-icon>
            </v-btn>
            <v-btn
              v-if="item.status === 'pending_review'"
              icon
              small
              color="warning"
              @click="ignoreDetection(item)"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>

      <!-- View Detection Dialog -->
      <v-dialog v-model="showViewDialog" max-width="900px">
        <v-card v-if="selectedDetection">
          <v-card-title class="headline">
            Duplicate Detection Details
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <strong>Record Type:</strong> {{ formatRecordType(selectedDetection.record_type) }}
              </v-col>
              <v-col cols="12" md="6">
                <strong>Similarity Score:</strong> {{ Math.round(selectedDetection.similarity_score || 0) }}%
              </v-col>
              <v-col cols="12" md="6">
                <strong>High Confidence:</strong> 
                <v-icon :color="selectedDetection.is_high_confidence ? 'green' : 'grey'" class="ml-1">
                  {{ selectedDetection.is_high_confidence ? 'mdi-check-circle' : 'mdi-minus-circle' }}
                </v-icon>
              </v-col>
              <v-col cols="12" md="6">
                <strong>Status:</strong>
                <v-chip
                  :color="getStatusColor(selectedDetection.status)"
                  dark
                  small
                  label
                  class="ml-2"
                >
                  {{ formatStatus(selectedDetection.status) }}
                </v-chip>
              </v-col>
            </v-row>
            
            <v-divider class="my-4"></v-divider>
            
            <div v-if="selectedDetection.duplicate_fields" class="mb-4">
              <h3 class="mb-2">Field Comparison</h3>
              <v-card outlined>
                <v-card-text>
                  <div v-for="(comparison, field) in selectedDetection.duplicate_fields" :key="field" class="field-comparison mb-2">
                    <strong>{{ field }}:</strong> {{ comparison }}
                  </div>
                </v-card-text>
              </v-card>
            </div>
            
            <div class="mb-4">
              <h3 class="mb-2">Suggested Action</h3>
              <v-chip :color="getActionColor(selectedDetection.suggested_action)" dark>
                {{ formatAction(selectedDetection.suggested_action) }}
              </v-chip>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="closeViewDialog">Close</v-btn>
            <v-btn 
              v-if="selectedDetection.status === 'pending_review'"
              color="warning" 
              @click="ignoreDetection(selectedDetection)"
            >
              Ignore
            </v-btn>
            <v-btn 
              v-if="selectedDetection.status === 'pending_review'"
              color="success" 
              @click="mergeRecords(selectedDetection)"
            >
              Merge Records
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "CRMGenieDuplicates",
  data() {
    return {
      isLoading: false,
      isScanning: false,
      showViewDialog: false,
      selectedDetection: null,
      search: '',
      detections: [],
      stats: {
        total_detections: 0,
        high_confidence_matches: 0,
        merged: 0,
        estimated_time_saved_hours: 0,
      },
      filters: {
        status: null,
        record_type: null,
        similarity_threshold: null,
        high_confidence_only: false,
      },
      headers: [
        { text: 'Detection', value: 'detection_name', sortable: true },
        { text: 'Record Type', value: 'record_type', sortable: false },
        { text: 'Similarity', value: 'similarity_score', sortable: true },
        { text: 'High Confidence', value: 'is_high_confidence', sortable: false },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Created', value: 'created_at', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
      statusOptions: [
        { text: 'Pending Review', value: 'pending_review' },
        { text: 'Reviewed', value: 'reviewed' },
        { text: 'Merged', value: 'merged' },
        { text: 'Ignored', value: 'ignored' },
        { text: 'Rejected', value: 'rejected' },
      ],
      recordTypeOptions: [
        { text: 'Contact', value: 'contact' },
        { text: 'Company', value: 'company' },
        { text: 'Deal', value: 'deal' },
        { text: 'Account', value: 'account' },
      ],
      similarityOptions: [
        { text: '90%+ (High)', value: 90 },
        { text: '80%+ (Medium)', value: 80 },
        { text: '70%+ (Low)', value: 70 },
      ],
    };
  },
  async mounted() {
    await this.fetchDetections();
    await this.fetchStats();
  },
  methods: {
    async fetchDetections() {
      try {
        this.isLoading = true;
        const params = { ...this.filters };
        
        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key];
          }
        });

        const response = await axios.get('crm-duplicate-detections', { params });
        
        if (response.data.success) {
          this.detections = response.data.data;
        }
      } catch (error) {
        console.error('Error fetching detections:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to load duplicate detections. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },

    async fetchStats() {
      try {
        const response = await axios.get('crm-duplicate-detections/stats');
        if (response.data.success) {
          this.stats = response.data.data.overview || {};
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    },

    async scanForDuplicates() {
      try {
        this.isScanning = true;
        const response = await axios.post('crm-duplicate-detections/scan', {
          scan_name: `Duplicate Scan - ${new Date().toLocaleString()}`,
          record_types: ['contact', 'company'],
          similarity_threshold: 80,
        });
        
        if (response.data.success) {
          this.$store.dispatch('errors/setSuccess', 'Duplicate scan started successfully');
          setTimeout(() => {
            this.fetchDetections();
            this.fetchStats();
          }, 2000);
        }
      } catch (error) {
        console.error('Error scanning for duplicates:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to start duplicate scan. Please try again.']
        });
      } finally {
        this.isScanning = false;
      }
    },

    async mergeRecords(detection) {
      try {
        this.isLoading = true;
        const response = await axios.post(`crm-duplicate-detections/${detection.id}/merge`);
        
        if (response.data.success) {
          this.$store.dispatch('errors/setSuccess', 'Records merged successfully');
          await this.fetchDetections();
          await this.fetchStats();
          this.closeViewDialog();
        }
      } catch (error) {
        console.error('Error merging records:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to merge records. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },

    async ignoreDetection(detection) {
      try {
        this.isLoading = true;
        const response = await axios.post(`crm-duplicate-detections/${detection.id}/ignore`);
        
        if (response.data.success) {
          this.$store.dispatch('errors/setSuccess', 'Detection ignored successfully');
          await this.fetchDetections();
          this.closeViewDialog();
        }
      } catch (error) {
        console.error('Error ignoring detection:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to ignore detection. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },

    viewDetection(detection) {
      this.selectedDetection = detection;
      this.showViewDialog = true;
    },

    closeViewDialog() {
      this.showViewDialog = false;
      this.selectedDetection = null;
    },

    getStatusColor(status) {
      const colors = {
        'pending_review': 'orange',
        'reviewed': 'blue',
        'merged': 'green',
        'ignored': 'grey',
        'rejected': 'red',
      };
      return colors[status] || 'grey';
    },

    getSimilarityColor(score) {
      if (score >= 90) return 'green';
      if (score >= 80) return 'orange';
      return 'red';
    },

    getActionColor(action) {
      const colors = {
        'merge': 'green',
        'ignore': 'orange',
        'review': 'blue',
      };
      return colors[action] || 'grey';
    },

    formatStatus(status) {
      return status.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    },

    formatRecordType(type) {
      return type.charAt(0).toUpperCase() + type.slice(1);
    },

    formatAction(action) {
      return action.charAt(0).toUpperCase() + action.slice(1);
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
  },
};
</script>

<style scoped>
.duplicates {
  min-height: calc(100vh - 64px);
}

.page-header {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.title-icon {
  color: #667eea;
}

.page-subtitle {
  font-size: 16px;
  color: #64748b;
  margin: 8px 0 0 0;
  font-weight: 400;
}

.scan-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  height: 48px !important;
}

.stats-card {
  border-radius: 12px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
}

.stats-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.15) !important;
}

.stats-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px !important;
}

.stats-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.orange--bg { background: linear-gradient(135deg, #ff9500 0%, #ff6b00 100%); }
.red--bg { background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%); }
.green--bg { background: linear-gradient(135deg, #51cf66 0%, #40c057 100%); }
.purple--bg { background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%); }

.stats-text {
  flex: 1;
}

.stats-value {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
  margin-bottom: 4px;
}

.stats-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filters-card {
  border-radius: 12px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.similarity-score {
  min-width: 100px;
}

.score-text {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.field-comparison {
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #667eea;
}

@media (max-width: 960px) {
  .header-content {
    flex-direction: column;
    text-align: center;
  }
  
  .page-title {
    font-size: 28px;
    justify-content: center;
  }
  
  .page-header {
    padding: 24px 20px;
  }
}

@media (max-width: 600px) {
  .page-header {
    padding: 20px 16px;
  }
  
  .page-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }
  
  .scan-btn {
    width: 100%;
  }
}
</style>